import { UserRole } from '@/types';

/** Customer org admin (tenant owner) — not platform SUPER_ADMIN. */
export function isOrgAdminRole(role?: string | null): boolean {
  return role === UserRole.ADMIN;
}

/** First-time welcome — pick org products. */
export const ORG_SETUP_PATH = '/org/setup';

/** Org admin home after setup — workspace dashboard. */
export const ORG_HOME_PATH = '/org';

/** Org admin products & seats. */
export const ORG_PRODUCTS_PATH = '/org/products';

/** Org security & governance UI. */
export const ORG_SECURITY_PATH = '/org/security';

/** @deprecated Use ORG_HOME_PATH */
export const ORG_ADMIN_HOME = ORG_HOME_PATH;

export interface OrgSetupState {
  completedAt?: string;
  version?: number;
}

export function readOrgSetup(settings?: Record<string, unknown> | null): OrgSetupState | null {
  const raw = settings?.orgSetup;
  if (!raw || typeof raw !== 'object') return null;
  return raw as OrgSetupState;
}

export function isOrgSetupComplete(settings?: Record<string, unknown> | null): boolean {
  return Boolean(readOrgSetup(settings)?.completedAt);
}

export function needsOrgSetup(role?: string | null, settings?: Record<string, unknown> | null): boolean {
  return isOrgAdminRole(role) && !isOrgSetupComplete(settings);
}

export function getOrgAdminLanding(settings?: Record<string, unknown> | null): string {
  // Always land on the org console home — setup wizard is an optional guided tour,
  // not a mandatory gate. Admins can start the tour from the org home if they want.
  return ORG_HOME_PATH;
}

/**
 * Plan delivery routes — Board, Backlog, projects, etc.
 * Org admin sees product menus only on these paths.
 */
const PLAN_DELIVERY_PREFIXES = [
  '/dashboard',
  '/projects',
  '/board',
  '/list',
  '/backlog',
  '/milestones',
  '/handbook',
  '/wiki',
  '/calendar',
  '/gantt',
  '/workload',
  '/reports',
  '/stakeholder',
  '/economics',
  '/cost',
  '/ai',
  '/ai-studio',
  '/ai-whiteboard',
  '/ai-assistant',
  '/sprints',
  '/time-tracking',
  '/admin/org-settings',
  '/task/',
  '/tasks/',
  '/userstory/',
  '/epic/',
  '/feature/',
  '/bug/',
  '/pm/',
];

const SATELLITE_PRODUCT_PREFIXES = ['/code', '/ship', '/lenc'];

export function isPlanProductPath(pathname: string): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/$/, '') || '/';
  return PLAN_DELIVERY_PREFIXES.some((prefix) => {
    const p = prefix.replace(/\/$/, '');
    return path === p || path.startsWith(`${p}/`);
  });
}

export function isSatelliteProductPath(pathname: string): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/$/, '') || '/';
  return SATELLITE_PRODUCT_PREFIXES.some((prefix) => {
    const p = prefix.replace(/\/$/, '');
    return path === p || path.startsWith(`${p}/`);
  });
}

/** Plan or satellite product routes (not org console). */
export function isPlanDeliveryPath(pathname: string): boolean {
  return isPlanProductPath(pathname) || isSatelliteProductPath(pathname);
}

/** Org-console routes — administration only (no Plan board/backlog chrome). */
const ORG_CONSOLE_PREFIXES = [
  ORG_SETUP_PATH,
  ORG_HOME_PATH,
  ORG_PRODUCTS_PATH,
  '/org/licenses',
  ORG_SECURITY_PATH,
  '/members',
  '/billing',
  '/admin/audit-logs',
  '/integrations',
  '/sso-config',
  '/webhooks',
  '/workflows',
  '/workspace',
  '/admin/org-settings',
];

export function isOrgConsolePath(pathname: string): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/$/, '') || '/';
  return ORG_CONSOLE_PREFIXES.some((prefix) => {
    const p = prefix.replace(/\/$/, '');
    return path === p || path.startsWith(`${p}/`);
  });
}

/**
 * Org-admin console chrome: org menus only (no Board/Backlog).
 * Platform SUPER_ADMIN uses platformMode instead.
 */
export function shouldUseOrgConsoleNav(role?: string | null, pathname?: string): boolean {
  if (!isOrgAdminRole(role)) return false;
  if (!pathname) return true;
  if (isPlanDeliveryPath(pathname)) return false;
  return isOrgConsolePath(pathname);
}
